import { Colors, Icon, InlineChildren, Spacing, Text } from '@doordash/design-language';
import CloseButton from 'components/buttons/CloseButton';
import breakpoints from 'constants/breakpoints';
import { button, header } from 'constants/style';
import { ReactNode } from 'react';
import styled from 'styled-components';
import { Alert, Modal, Typography } from 'top-component-library';

const { None, Small, XSmall, XxSmall, XxxSmall, XxxxSmall, Medium } = Spacing;

export const CartCloseButton = styled(CloseButton)`
  position: absolute;
  right: 1rem;
  top: 1rem;
  z-index: ${XxxxSmall + XxSmall};
`;
export const CloseOnTouch = styled.div`
  background-color: transparent;
  cursor: pointer;
  height: ${header.mobile.height + 10}px;
  position: absolute;
  top: -${header.mobile.height + 10}px;
  width: 100%;
`;
export const Container = styled.div`
  display: flex;
  flex: 1;
  flex-direction: column;
  height: 100%;
  border-top-right-radius: 24px;
  border-top-left-radius: 24px;

  @media (min-width: ${breakpoints.md}px) {
    flex-direction: row;
    height: initial;
    max-height: 67vh;
  }
`;
export const Description = styled.p`
  color: var(--bose-item__item-subtitle);
  font-size: 0.875rem;
  margin-bottom: ${None};
  margin-top: 0.5rem;
  opacity: 0.8;
  padding-right: 2.5rem;
  font-weight: 400;
  line-height: ${Spacing.Medium}px;
  letter-spacing: 0.75px;

  @media (min-width: ${breakpoints.md}px) {
    margin-top: 1rem;
    opacity: 1;
  }

  @media (min-width: ${breakpoints.lg}px) {
    padding-right: 5rem;
  }
`;
export const FixedHeader = styled.div<{ visible: boolean; hasImage: boolean }>`
  align-items: center;
  display: flex;
  height: 3.25rem;
  opacity: ${({ visible }) => (visible ? 1 : 0)};
  padding-left: 1.25rem;
  position: fixed;
  top: -3.25rem;
  transform: translateY(${({ visible }) => (visible ? '3.25rem' : 0)});
  transition: opacity 0.15s ease, transform 0.3s ease;
  width: 100%;
  z-index: ${Medium};

  @media (min-width: ${breakpoints.md}px) {
    position: absolute;
    right: ${None};
    transition: initial;
    opacity: 0;
    width: ${({ hasImage }) => (hasImage ? '50%' : '100%')};
  }
`;
export const CartImage = styled.img`
  height: 100%;
  object-fit: cover;
  width: 100%;
  border-top-right-radius: 24px;
  border-top-left-radius: 24px;
  @media (min-width: ${breakpoints.md}px) {
    border-top-right-radius: var(--border-radius__small);
    border-top-left-radius: var(--border-radius__small);
  }
`;
export const ImageWrapper = styled.div`
  height: 240px;

  @media (min-width: ${breakpoints.md}px) {
    flex: 1;
    height: auto;
    margin-top: ${None};
  }
`;
export const Loader = styled.div`
  align-items: center;
  display: flex;
  height: 250px;
  justify-content: center;
`;
export const LockedCartWarning = styled(Alert)`
  margin: 1rem 1.25rem 1rem 1.25rem;

  &&&.ant-alert {
    background-color: var(--tabs-background-color__secondary);
    border-color: var(--color-neutral__line);
    text-align: center;
  }

  .ant-alert-message {
    font-style: normal;
    font-weight: 400;
    font-size: ${XSmall + XxxxSmall}px;
    line-height: ${Small + XxxxSmall}px;
    letter-spacing: 0.75px;
    color: var(--tabs-text-color__primary);
  }
`;
export const LowStockNote = styled.em`
  color: var(--color-error__regular);
  display: block;
  letter-spacing: 1px;
  margin-top: 0.5rem;
`;
export const Main = styled.div`
  background: var(--color-neutral__modal-background);
  border-top-right-radius: 24px;
  border-top-left-radius: 24px;
  @media (min-width: ${breakpoints.md}px) {
    border-top-right-radius: var(--border-radius__small);
    border-top-left-radius: var(--border-radius__small);
  }
  position: relative;

  @media (min-width: ${breakpoints.md}px) {
    border-top-left-radius: ${None};
    border-top-right-radius: ${None};
    display: flex;
    flex: 1;
    flex-direction: column;
    overflow: hidden;
  }
`;
export const MainHeader = styled.div<{ hasImage: boolean }>`
  display: flex;
  flex-shrink: ${None};
  justify-content: space-between;
  margin-bottom: 1.25rem;
  padding: ${None} 1.25rem;
  padding-top: ${({ hasImage }) => (hasImage ? '0rem' : '1.25rem')};
  @media (min-width: ${breakpoints.md}px) {
    margin-bottom: 1rem;
    padding: ${None} 1.5rem;
  }
`;
export const ItemInnerContent = styled.div<{ hasImage: boolean; enabledCheckout: boolean }>`
  display: flex;
  flex: 1;
  flex-direction: column;
  height: 100%;
  overflow: hidden;
  padding: ${({ hasImage }) => (hasImage ? '1.25rem' : '3.25rem')} ${None} ${button.regular + 26}px ${None};
  position: relative;
  z-index: ${None};

  @media (min-width: ${breakpoints.md}px) {
    padding-bottom: ${({ enabledCheckout }) => (enabledCheckout ? `${None}` : '3.25rem')};
    padding-top: 3.25rem;

    // Transparency effect at the top of the container
    &::before {
      background: linear-gradient(to bottom, var(--color-neutral__modal-background) 0%, rgba(255, 255, 255, 0) 100%);
      content: '';
      height: 0.25rem;
      position: absolute;
      width: calc(100% - 3.75rem);
    }
  }
`;
export const CartModal = styled(Modal)<{ children: ReactNode }>`
  background: var(--color-neutral__modal-background);
  border-top-left-radius: 24px;
  border-top-right-radius: 24px;
  bottom: ${None};
  left: ${None};
  margin: ${header.mobile.height + 10}px ${None} ${None};
  max-width: 100%;
  padding: ${None};
  position: absolute;

  .ant-modal-content {
    height: 100%;
  }

  .ant-modal-body,
  .ant-modal-content {
    background-color: transparent;
    display: flex;
    flex: 1;
    flex-direction: column;
    padding: ${None};
  }

  @media (min-width: ${breakpoints.md}px) {
    margin: 0;
    max-width: calc(100vw - ${XxxxSmall}rem);
    position: relative;
    border-radius: var(--border-radius__small);
    overflow: hidden;

    .ant-modal-body,
    .ant-modal-content {
      height: initial;
    }

    .ant-modal-content {
      border-radius: var(--border-radius__small);
    }
  }
`;
export const Price = styled.span<{ hasDescription: boolean }>`
  align-items: flex-end;
  display: flex;
  font-weight: bold;
  font-size: 0.875rem;
  letter-spacing: 1px;
  line-height: ${({ hasDescription }) => (hasDescription ? '1.3rem' : '1.6rem')};

  @media (min-width: ${breakpoints.md}px) {
    line-height: ${({ hasDescription }) => (hasDescription ? '1.4rem' : '1.8rem')};
  }
`;
export const Title = styled(Typography.Title)`
  // Main Header Title
  && {
    font-size: 1.25rem;
    font-weight: 500;
    line-height: 1.75rem;
    margin-bottom: ${None};
    padding-right: 2.5rem;

    @media (min-width: ${breakpoints.md}px) {
      font-size: 1.5rem;
      line-height: ${XxxxSmall}rem;
    }

    @media (min-width: ${breakpoints.lg}px) {
      padding-right: 5rem;
    }
  }

  // Fixed Header Title
  h3&& {
    font-size: 1.125rem;
    overflow: hidden;
    padding-right: 5rem;
    padding-top: 1px;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
`;

export const OptionalGroup = styled.span`
  background-color: ${Colors.BackgroundTertiary};
  border-radius: ${XxxSmall}px;
  padding-left: ${XxxSmall}px;
  padding-right: ${XxxSmall}px;
  display: flex;
  align-items: center;
`;

export const ModifierGroupHeading = styled(InlineChildren)`
  display: flex;
  justify-content: space-between;
`;

export const RequiredGroup = styled(InlineChildren)`
  background-color: #ffffa3;
  color: ${Colors.SystemYellow80};
  padding-left: ${XxxSmall}px;
  padding-right: ${XxxSmall}px;
  display: flex;
  justify-content: center;
  align-items: center;
  border-radius: ${XxxSmall}px;
  margin-top: -${XxxSmall}px;

  svg {
    margin-right: -${Small}px;
  }
`;

export const WarningIcon = styled(Icon).attrs({
  type: Icon.Types.WarningFill,
  color: '#643B00',
  size: Icon.Sizes.Small,
})``;

export const Spacer = styled.div`
  border-top: 6px solid ${Colors.BackgroundSecondary};
  margin-bottom: ${Spacing.XLarge}px;
`;

export const RecommendationCarouselWrapper = styled.div`
  margin: ${None} ${Spacing.Large}px ${Spacing.Large}px ${Spacing.Large}px;
`;

export const StyledTitle = styled(Text)`
  color: var(--bose-item__item-title);
  margin: 0;
`;

export const StyledSpecialInstructionTitle = styled(Text)`
  color: var(--bose-item__modifier-option-text);
`;
